/**
 * Client de l'API (ADR 0007) : chiffrage des mesures dictées et télémétrie de l'observatoire. Même origine par défaut
 * (le conteneur sert l'UI et /api/*) ; base surchargeable au build par `BFG_API_BASE=https://… node ui/build.mjs`
 * (`define: __BFG_API_BASE__`).
 *
 * Détection : l'API n'est sondée (`/api/health`) que si la page la déclare — balise `<meta name="bfg-api">` injectée
 * dans index.html par le conteneur (backend/src/server.ts) — ou si une base a été fixée au build. Le site statique seul
 * (GitHub Pages, `node ui/build.mjs` + serveur de fichiers) ne fait donc AUCUNE requête /api (pas de 404 en console).
 * Si l'API n'est pas joignable (`/api/health` en échec ou `estimate: false`), `estimateMeasure` rejette
 * `EstimateUnavailableError` sans message → ui/src/proposals.ts garde l'état désactivé (libellé d'origine).
 */
import type { Measure, MeasureCategory } from "../../engine/src/types.js";

declare const __BFG_API_BASE__: string | undefined;
const BUILD_BASE: string | null = typeof __BFG_API_BASE__ === "string" && __BFG_API_BASE__ ? __BFG_API_BASE__.replace(/\/$/, "") : null;
const API_BASE: string = BUILD_BASE ?? "";

/** L'API est-elle déclarée pour cette page (meta injectée par le conteneur, ou base fixée au build) ? */
export function apiDeclared(): boolean {
  if (BUILD_BASE !== null) return true;
  try {
    return typeof document !== "undefined" && document.querySelector('meta[name="bfg-api"]') !== null;
  } catch {
    return false;
  }
}

/** Source d'un chiffrage sourcé (ADR 0013) : URL issue des résultats des outils de recherche, citation courte, valeur lue. */
export interface SourcedSource {
  url: string;
  title: string;
  publisher: string;
  accessed: string;
  quote: string;
  value_read: string;
  value_bn: number;
  year?: number;
  quote_verified: boolean;
}

/** Chiffrage sourcé (tier B) tel que renvoyé par l'API. */
export interface SourcedInfo {
  status: "estimé_ia_source" | "estimé_ia_une_source" | "non_chiffrable";
  sources: SourcedSource[];
  method: Array<{ kind: "input" | "calc"; label: string; value: number; unit: string; source_index: number | null; formula?: string }>;
  uncertainty: string;
  explanation?: string;
  independent_publishers: number;
  derived_from?: string;
  model: string;
  created_at?: string;
}

/** Suivi du chiffrage sourcé en tâche de fond ; délais fournis par le serveur (aucune constante côté UI). */
export interface SourcedJob {
  id: string;
  status: "queued" | "running" | "done" | "failed" | "deferred";
  message: string;
  poll_after_ms: number;
  poll_max_ms: number;
  eta_s: number;
}

/** Même forme que `EstimatedMeasure` de ui/src/proposals.ts, plus l'objet `Measure` complet pour le moteur. */
export interface EstimatedMeasure {
  text: string;
  category: MeasureCategory;
  label: string;
  cost_bn: { low: number; central: number; high: number };
  hyp?: { label: string; low: number; high: number; default: number };
  people?: number;
  rationale: string;
  /** `estimé_ia` = aperçu provisoire non sourcé ; `estimé_ia_source` / `estimé_ia_une_source` = chiffrage sourcé (ADR 0013). */
  status: Measure["status"];
  /** `provisional` = aperçu tier A (jamais final) ; `sourced` = chiffrage stocké une fois pour toutes. */
  tier: "provisional" | "sourced";
  /** Chemin côté serveur : `exact` / `judge` / `formula` = déjà chiffrée (zéro appel) ; `new` = premier passage. */
  path: "exact" | "judge" | "formula" | "new" | "pending";
  proposal_id: string | null;
  /** « vu X fois » (toutes sessions). */
  hit_count: number;
  sourced?: SourcedInfo;
  job?: SourcedJob;
  /** Message du serveur à afficher tel quel (chiffrage en cours, reporté, plafond…). */
  notice?: string;
  /** Id de l'aperçu provisoire que cette version sourcée remplace (pour le retirer du programme à l'ajout). */
  replaces?: string;
  /** Effets prix directs (fx.prices de la mesure, Δ % en régime), pour l'aperçu de la carte d'estimation. */
  price_effects?: Partial<Record<"bread" | "fuel" | "rent" | "energy", number>>;
  /** Mesure prête pour le moteur (statut `estimé_ia`, validée par le schéma partagé côté serveur). */
  measure: Measure;
  /** Clé de cache (relie l'événement `measure_dictated` à la file de revue). */
  key: string;
  cached: boolean;
  anchors: Array<{ id: string; label: string }>;
}

/**
 * Service de chiffrage indisponible. `transient = false` : pas d'API ou chiffrage non ouvert (`estimate: false`) → l'UI
 * garde la carte désactivée et son libellé d'origine ; `true` : API ouverte mais en échec (réseau, 5xx) → message du serveur.
 */
export class EstimateUnavailableError extends Error {
  readonly transient: boolean;
  constructor(message = "service indisponible", transient = false) {
    super(message);
    this.name = "EstimateUnavailableError";
    this.transient = transient;
  }
}
/** Refus motivé (hors périmètre, nom de candidat, injection, estimation non conforme) : `message` est à afficher tel quel. */
export class EstimateRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EstimateRefusedError";
  }
}
/** Plafond atteint (session, réseau ou budget quotidien) : `message` en français, à afficher tel quel. */
export class EstimateRateLimitedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EstimateRateLimitedError";
  }
}

const SESSION_KEY = "bfg.session.v1";

/** Identifiant de session anonyme : UUID aléatoire en sessionStorage (pas de cookie), oublié à la fermeture de l'onglet. */
export function sessionId(): string {
  try {
    const s = sessionStorage.getItem(SESSION_KEY);
    if (s) return s;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export interface ApiHealth {
  ok: boolean;
  estimate: boolean;
  telemetry: boolean;
  build?: string;
  versions?: { engine: string; params: string; content: string; prompt: string };
}

let healthPromise: Promise<ApiHealth | null> | null = null;

/** Détection de disponibilité (une fois par chargement de page). null = pas d'API (site statique seul). */
export function apiHealth(): Promise<ApiHealth | null> {
  if (!apiDeclared()) return Promise.resolve(null);
  healthPromise ??= fetch(`${API_BASE}/api/health`, { headers: { accept: "application/json" } })
    .then((r) => (r.ok ? (r.json() as Promise<ApiHealth>) : null))
    .then((h) => (h && h.ok === true ? h : null))
    .catch(() => null);
  return healthPromise;
}

export interface ApiEstimateOk {
  refused: false;
  cached: boolean;
  key: string;
  measure: Measure;
  estimate: { assiette_id: string; people: number; rationale: string; anchors: Array<{ id: string; label: string }>; model: string; prompt_version: string };
  /** Champs ADR 0013 (absents d'une API v1 : aperçu seul). */
  tier?: "provisional" | "sourced";
  path?: EstimatedMeasure["path"];
  proposal_id?: string | null;
  hit_count?: number;
  sourced?: SourcedInfo;
  job?: SourcedJob;
  notice?: string;
}

function scalar(p: Measure["cost"] | undefined, fallback: number): number {
  if (p === undefined) return fallback;
  return Array.isArray(p) ? (p[p.length - 1] ?? fallback) : p;
}

export async function estimateMeasure(text: string, category: MeasureCategory): Promise<EstimatedMeasure> {
  const h = await apiHealth();
  if (!h?.estimate) throw new EstimateUnavailableError();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/estimate`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-session-id": sessionId() },
      body: JSON.stringify({ text, category }),
    });
  } catch {
    throw new EstimateUnavailableError("Le chiffrage par IA ne répond pas (réseau).", true);
  }
  const body = (await res.json().catch(() => null)) as (ApiEstimateOk | { refused: true; reason: string } | { error: string; message: string }) | null;
  if (res.status === 429) throw new EstimateRateLimitedError((body as { message?: string } | null)?.message ?? "Limite atteinte pour aujourd'hui.");
  if (!res.ok || !body) throw new EstimateUnavailableError((body as { message?: string } | null)?.message ?? "Le chiffrage par IA n'est pas disponible pour le moment.", true);
  if ("refused" in body && body.refused) throw new EstimateRefusedError(body.reason);
  return toEstimated(text, category, body as ApiEstimateOk);
}

/** Réponse de l'API → carte d'estimation (pur, testé hors navigateur). */
export function toEstimated(text: string, category: MeasureCategory, ok: ApiEstimateOk): EstimatedMeasure {
  const m = ok.measure;
  const central = scalar(m.cost, 0);
  return {
    text,
    category,
    label: m.label,
    cost_bn: { low: scalar(m.cost_low, central), central, high: scalar(m.cost_high, central) },
    ...(m.hyp ? { hyp: { label: m.hyp.label, low: m.hyp.low, high: m.hyp.high, default: m.hyp.default } } : {}),
    ...(ok.estimate.people > 0 ? { people: ok.estimate.people } : {}),
    ...(m.fx.prices ? { price_effects: Object.fromEntries(Object.entries(m.fx.prices).map(([k, v]) => [k, scalar(v, 0)])) } : {}),
    rationale: ok.estimate.rationale,
    status: m.status,
    tier: ok.tier ?? "provisional",
    path: ok.path ?? "new",
    proposal_id: ok.proposal_id ?? null,
    hit_count: ok.hit_count ?? 1,
    ...(ok.sourced ? { sourced: ok.sourced } : {}),
    ...(ok.job ? { job: ok.job } : {}),
    ...(ok.notice ? { notice: ok.notice } : {}),
    measure: m,
    key: ok.key,
    cached: ok.cached,
    anchors: ok.estimate.anchors,
  };
}

/** Suivi d'un chiffrage sourcé : GET /api/estimate/:job_id. `result` présent quand le job est terminé. */
export async function fetchSourcedJob(jobId: string, text: string, category: MeasureCategory): Promise<{ job: SourcedJob; result?: EstimatedMeasure; refused?: string } | null> {
  if (!/^[0-9a-f-]{36}$/i.test(jobId)) return null;
  try {
    const res = await fetch(`${API_BASE}/api/estimate/${jobId}`, { headers: { accept: "application/json" } });
    if (!res.ok) return null;
    const b = (await res.json()) as { job: SourcedJob; result?: ApiEstimateOk | { refused: true; reason: string } };
    if (!b.result) return { job: b.job };
    if ("refused" in b.result && b.result.refused) return { job: b.job, refused: b.result.reason };
    return { job: b.job, result: toEstimated(text, category, b.result as ApiEstimateOk) };
  } catch {
    return null;
  }
}

/** Lot d'événements (forme : content/schema/telemetry.schema.json). `consent: true` est exigé par le type ET par le serveur. */
export interface TelemetryBatch {
  session_id: string;
  consent: true;
  engine_version: string;
  params_version: string;
  content_version?: string;
  events: Array<Record<string, unknown> & { type: string; seq: number }>;
}

/** Envoi de télémétrie : jamais bloquant, jamais d'exception. Renvoie true si le lot a été accepté. */
export async function sendEvents(batch: TelemetryBatch): Promise<boolean> {
  if (batch.consent !== true || batch.events.length === 0) return false;
  const h = await apiHealth();
  if (!h?.telemetry) return false;
  try {
    const res = await fetch(`${API_BASE}/api/events`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(batch), keepalive: true });
    return res.status === 202;
  } catch {
    return false;
  }
}
